import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { authApi } from "../services/api";
import Logo from "../components/Logo";
import Button from "../components/Button";
import Input from "../components/Input";
import LanguageSwitcher from "../components/LanguageSwitcher";

const T = { night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3" };

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || "/dashboard";

  const [form, setForm]     = useState({ phoneNumber:"", password:"" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handle = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const e = {};
    if (!form.phoneNumber) e.phoneNumber = t("login.required");
    if (!form.password)    e.password    = t("login.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await authApi.login(form);
      const { data } = res.data;
      if (data.otpRequired) {
        navigate("/verify-otp", { state: { phoneNumber: form.phoneNumber } });
        return;
      }
      login(
        { userId:data.userId, firstName:data.firstName, lastName:data.lastName,
          role:data.role, countryCode:data.countryCode, phoneNumber:form.phoneNumber },
        data.accessToken, data.refreshToken
      );
      toast.success(t("login.welcome", { name: data.firstName }));
      navigate(from, { replace:true });
    } catch (err) {
      toast.error(err.response?.data?.message || t("login.error"));
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", fontFamily:"'DM Sans',sans-serif" }}>
      {/* Panel gauche */}
      <div style={{ display:"none", width:"45%", flexDirection:"column",
        justifyContent:"space-between", padding:"48px",
        background:`linear-gradient(160deg,${T.night} 0%,${T.royal} 60%,${T.cyan} 100%)` }}
        className="lg-panel">
        <Logo size="lg" white />
        <div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"40px",
            fontWeight:700, color:"#fff", lineHeight:1.3, marginBottom:"16px",
            whiteSpace:"pre-line" }}>
            {t("login.panel_title")}</h2>
          <p style={{ color:"rgba(255,255,255,0.6)", fontSize:"16px", lineHeight:1.7 }}>
            {t("login.panel_subtitle")}</p>
        </div>
        <p style={{ color:"rgba(255,255,255,0.25)", fontSize:"12px" }}>
          {t("login.copyright", { year: new Date().getFullYear() })}</p>
      </div>

      {/* Panel droit */}
      <div style={{ flex:1, display:"flex", alignItems:"center",
        justifyContent:"center", padding:"32px", background:"#F7F9FC" }}>
        <div style={{ width:"100%", maxWidth:"420px" }}>
          <div style={{ marginBottom:"32px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <Logo size="md" />
              <LanguageSwitcher />
            </div>
            <h1 style={{ fontSize:"26px", fontWeight:800, color:T.night,
              marginTop:"24px", marginBottom:"6px" }}>{t("login.title")}</h1>
            <p style={{ color:"#64748B", fontSize:"14px" }}>
              {t("login.subtitle")}</p>
          </div>

          <form onSubmit={submit}>
            <Input label={t("login.phone")} name="phoneNumber" type="tel"
              placeholder="+237 6 XX XX XX XX" value={form.phoneNumber}
              onChange={handle} error={errors.phoneNumber} required />

            <div style={{ position:"relative" }}>
              <Input label={t("login.password")} name="password"
                type={showPwd ? "text" : "password"} placeholder="••••••••"
                value={form.password} onChange={handle} error={errors.password} required />
              <button type="button" onClick={() => setShowPwd(!showPwd)}
                style={{ position:"absolute", right:"14px", top:"38px",
                  background:"none", border:"none", color:"#94A3B8",
                  cursor:"pointer", fontSize:"13px" }}>
                {showPwd ? t("login.hide") : t("login.show")}
              </button>
            </div>

            <div style={{ textAlign:"right", marginBottom:"24px" }}>
              <Link to="/forgot-password"
                style={{ color:T.cyan, fontSize:"13px", fontWeight:500, textDecoration:"none" }}>
                {t("login.forgot")}</Link>
            </div>

            <Button type="submit" variant="cyan" full loading={loading}>
              {t("login.submit")}
            </Button>
          </form>

          <div style={{ display:"flex", alignItems:"center", gap:"12px", margin:"20px 0" }}>
            <div style={{ flex:1, height:"1px", background:"#E2E8F0" }} />
            <span style={{ color:"#94A3B8", fontSize:"12px" }}>{t("login.or")}</span>
            <div style={{ flex:1, height:"1px", background:"#E2E8F0" }} />
          </div>

          <p style={{ textAlign:"center", fontSize:"14px", color:"#64748B" }}>
            {t("login.no_account")}{" "}
            <Link to="/register" style={{ color:T.cyan, fontWeight:600, textDecoration:"none" }}>
              {t("login.create")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
